import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updatePassword, onAuthStateChange, getAuthSession } from '@/lib/authService'
import { routes } from '@/lib/appConfig'
import { AuthLayout } from '@/shared/components/auth'
import { Button, Alert } from '@/shared/components/ui'
import { LockIcon, EyeIcon, EyeOffIcon } from '@/shared/components/ui/Icons'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useTheme } from '@/shared/contexts/ThemeContext'

// Will compute per-theme inside component to respect dark mode

export default function ResetPassword() {
  const { t }     = useTranslation()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const navigate  = useNavigate()

  const [ready,       setReady]       = useState(false)
  const [password,    setPassword]    = useState('')
  const [confirm,     setConfirm]     = useState('')
  const [showPwd,     setShowPwd]     = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoading,   setLoading]     = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [success,     setSuccess]     = useState(false)

  // Listen to auth state changes and check for existing session
  useEffect(() => {
    const unsubscribe = onAuthStateChange((event: string) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setReady(true)
      }
    })

    // Also mark ready if there is already a session (page reload after hash exchange)
    getAuthSession().then((session) => {
      if (session) setReady(true)
    })

    return () => unsubscribe()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 6) {
      setError(t('auth.errorPasswordLength'))
      return
    }
    if (password !== confirm) {
      setError(t('auth.errorPasswordMismatch'))
      return
    }
    setLoading(true)
    try {
      const { error: err } = await updatePassword(password)
      if (err) throw err
      setSuccess(true)
      setTimeout(() => navigate(routes.login, { replace: true }), 2500)
    } catch (err: any) {
      setError(err.message || t('errors.defaultError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout subtitle={t('auth.resetPasswordSubtitle')}>
      {success ? (
        <Alert variant="success">{t('auth.resetPasswordSuccess')}</Alert>
      ) : !ready ? (
        <div className="flex justify-center py-8">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nova senha */}
          <div>
            <label htmlFor="password" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
              {t('auth.newPassword')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <LockIcon />
              </div>
              <input
                id="password"
                type={showPwd ? 'text' : 'password'}
                required
                className={`block w-full rounded-lg border-0 py-3 shadow-sm ring-1 ring-inset focus:ring-2 sm:text-sm ${isDark ? 'bg-slate-700 text-slate-100 ring-slate-600 placeholder:text-slate-400 focus:ring-emerald-500' : 'bg-white text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-green-600'} pl-10 pr-10`}
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                onClick={() => setShowPwd(v => !v)}
                aria-label={showPwd ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPwd
                  ? <EyeOffIcon className={`h-5 w-5 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`} />
                  : <EyeIcon    className={`h-5 w-5 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`} />}
              </button>
            </div>
          </div>

          {/* Confirmar senha */}
          <div>
            <label htmlFor="confirm" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
              {t('auth.confirmNewPassword')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <LockIcon />
              </div>
              <input
                id="confirm"
                type={showConfirm ? 'text' : 'password'}
                required
                className={`block w-full rounded-lg border-0 py-3 shadow-sm ring-1 ring-inset focus:ring-2 sm:text-sm ${isDark ? 'bg-slate-700 text-slate-100 ring-slate-600 placeholder:text-slate-400 focus:ring-emerald-500' : 'bg-white text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-green-600'} pl-10 pr-10`}
                placeholder={t('auth.passwordPlaceholder')}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                onClick={() => setShowConfirm(v => !v)}
                aria-label={showConfirm ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showConfirm
                  ? <EyeOffIcon className={`h-5 w-5 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`} />
                  : <EyeIcon    className={`h-5 w-5 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`} />}
              </button>
            </div>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <Button type="submit" fullWidth loading={isLoading}>
            {t('auth.resetPasswordButton')}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
